import * as originalApi from './api';
import * as originalTasks from './tasks';
import { PaginatedResponse, TaskFilters } from './tasks';
import cacheManager from './cacheManager';
import backgroundSyncService from './backgroundSync';
import indexedDBService from './indexedDB';
import { getCurrentUser, getToken } from './auth';
import { Task } from '../types/task';
import { Shipping } from '../types/shipping';

// Enhanced API wrapper that provides intelligent caching with cache-first strategy
class CachedApiService {
  private refreshCallbacks: Map<string, ((data: any) => void)[]> = new Map();

  // Subscribe to data updates - useful for reactive UI components
  onDataUpdate(key: string, callback: (data: any) => void): () => void {
    if (!this.refreshCallbacks.has(key)) {
      this.refreshCallbacks.set(key, []);
    }
    this.refreshCallbacks.get(key)!.push(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.refreshCallbacks.get(key);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) callbacks.splice(index, 1);
      }
    };
  }

  private notifyDataUpdate(key: string, data: any): void {
    const callbacks = this.refreshCallbacks.get(key);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  private async getCacheFirstData<T>(
    cacheKey: string,
    indexedDBGetter: () => Promise<T>,
    serverFetcher: () => Promise<T>,
    _maxAge: number = 10 * 60 * 1000, // 10 minutes default - increased from 5
    refreshThreshold: number = 5 * 60 * 1000 // 5 minutes default - increased from 2
  ): Promise<T> {
    try {
      // 1. First, try to get data from IndexedDB immediately
      const cachedData = await indexedDBGetter();
      
      // 2. Check if we have metadata about when this data was last updated
      const metadata = await indexedDBService.getMetadata(cacheKey);
      const now = Date.now();
      
      if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
        // We have cached data, return it immediately
        console.debug(`Cache hit for ${cacheKey}, returning cached data immediately`);
        
        // 3. Check if we should refresh in background
        if (metadata) {
          const age = now - metadata.lastUpdated;
          const shouldRefreshInBackground = age > refreshThreshold;
          
          if (shouldRefreshInBackground && navigator.onLine) {
            console.debug(`Background refresh triggered for ${cacheKey}`);
            this.backgroundRefresh(cacheKey, serverFetcher, indexedDBGetter);
          }
        } else {
          // No metadata, trigger a background refresh
          this.backgroundRefresh(cacheKey, serverFetcher, indexedDBGetter);
        }
        
        return cachedData;
      } else {
        // No cached data available, fetch from server
        console.debug(`No cached data for ${cacheKey}, fetching from server`);
        return await this.fetchAndCache(cacheKey, serverFetcher);
      }
    } catch (error) {
      console.warn(`Cache-first strategy failed for ${cacheKey}:`, error);
      // Fallback to server fetch
      try {
        return await serverFetcher();
      } catch (serverError) {
        console.error(`Server fetch also failed for ${cacheKey}:`, serverError);
        throw serverError;
      }
    }
  }

  private backgroundRefreshQueue = new Set<string>();
  
  private async backgroundRefresh<T>(
    cacheKey: string,
    serverFetcher: () => Promise<T>,
    indexedDBGetter: () => Promise<T>
  ): Promise<void> {
    // Prevent duplicate background refreshes for the same cache key
    if (this.backgroundRefreshQueue.has(cacheKey)) {
      console.debug(`Background refresh already queued for ${cacheKey}, skipping`);
      return;
    }

    try {
      this.backgroundRefreshQueue.add(cacheKey);
      
      // Use requestIdleCallback if available, fallback to setTimeout with longer delay
      const scheduleRefresh = (callback: () => void) => {
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(callback, { timeout: 5000 });
        } else {
          setTimeout(callback, 1000); // Increased from 100ms
        }
      };

      scheduleRefresh(async () => {
        try {
          // Check if user is still online and page is visible
          if (!navigator.onLine || document.hidden) {
            console.debug(`Skipping background refresh for ${cacheKey} - offline or hidden`);
            return;
          }

          const freshData = await serverFetcher();
          
          // Use faster comparison for arrays
          const currentCachedData = await indexedDBGetter();
          let hasChanges = false;
          
          if (Array.isArray(freshData) && Array.isArray(currentCachedData)) {
            // Quick length check first
            hasChanges = freshData.length !== currentCachedData.length;
            if (!hasChanges && freshData.length > 0) {
              // More efficient comparison for large arrays - check sample items
              const sampleSize = Math.min(3, freshData.length);
              for (let i = 0; i < sampleSize; i++) {
                const freshItem = JSON.stringify(freshData[i]);
                const cachedItem = JSON.stringify(currentCachedData[i]);
                if (freshItem !== cachedItem) {
                  hasChanges = true;
                  break;
                }
              }
            }
          } else {
            hasChanges = JSON.stringify(freshData) !== JSON.stringify(currentCachedData);
          }
          
          if (hasChanges) {
            console.debug(`Fresh data differs from cache for ${cacheKey}, updating cache`);
            await this.updateIndexedDB(cacheKey, freshData);
            
            // Notify subscribers of the update
            this.notifyDataUpdate(cacheKey, freshData);
          } else {
            console.debug(`No changes detected for ${cacheKey}, cache is up to date`);
            // Update metadata timestamp even if data hasn't changed
            await indexedDBService.updateMetadata(cacheKey, Date.now());
          }
        } catch (error) {
          console.warn(`Background refresh failed for ${cacheKey}:`, error);
        } finally {
          this.backgroundRefreshQueue.delete(cacheKey);
        }
      });
    } catch (error) {
      console.warn(`Failed to start background refresh for ${cacheKey}:`, error);
      this.backgroundRefreshQueue.delete(cacheKey);
    }
  }

  private async fetchAndCache<T>(cacheKey: string, serverFetcher: () => Promise<T>): Promise<T> {
    try {
      console.log(`Fetching fresh data for ${cacheKey} from server`);
      const freshData = await serverFetcher();
      console.log(`Successfully fetched data for ${cacheKey}:`, Array.isArray(freshData) ? `${freshData.length} items` : 'object');
      await this.updateIndexedDB(cacheKey, freshData);
      return freshData;
    } catch (error) {
      console.error(`Failed to fetch and cache data for ${cacheKey}:`, error);
      throw error;
    }
  }

  private async updateIndexedDB<T>(cacheKey: string, data: T): Promise<void> {
    try {
      // Update the appropriate IndexedDB store based on cache key
      if (cacheKey.includes('tasks') && Array.isArray(data)) {
        await indexedDBService.putTasks(data as unknown as Task[]);
      } else if (cacheKey.includes('shipments') && Array.isArray(data)) {
        await indexedDBService.putShipments(data as unknown as Shipping[]);
      } else if (cacheKey.includes('quotes') && Array.isArray(data)) {
        await indexedDBService.putQuotes(data as unknown as any[]);
      } else if (cacheKey.includes('contacts') && Array.isArray(data)) {
        await indexedDBService.putContacts(data as unknown as any[]);
      }
      
      // Update metadata
      await indexedDBService.updateMetadata(cacheKey, Date.now());
    } catch (error) {
      console.error(`Failed to update IndexedDB for ${cacheKey}:`, error);
    }
  }

  // Task operations with cache-first strategy
  async getTasks(filters?: TaskFilters): Promise<Task[] | PaginatedResponse<Task>> {
    const cacheKey = `tasks_${JSON.stringify(filters || {})}`;
    const token = getToken();
    
    if (!token) {
      console.error('No authentication token available for getTasks');
      throw new Error('Authentication required');
    }
    
    return this.getCacheFirstData(
      cacheKey,
      () => indexedDBService.getTasks(),
      () => {
        console.log('Fetching tasks from server with token:', token ? 'present' : 'missing');
        return originalTasks.getTasks(filters);
      }
    );
  }

  async getAllTasks(): Promise<Task[] | PaginatedResponse<Task>> {
    const cacheKey = 'tasks_all';
    const token = getToken();
    
    if (!token) {
      console.error('No authentication token available for getAllTasks');
      throw new Error('Authentication required');
    }
    
    return this.getCacheFirstData(
      cacheKey,
      () => indexedDBService.getTasks(),
      () => {
        console.log('Fetching all tasks from server with token:', token ? 'present' : 'missing');
        return originalTasks.getAllTasks();
      }
    );
  }

  async getTasksByShipment(shipmentId: string): Promise<Task[] | PaginatedResponse<Task>> {
    const cacheKey = `tasks_shipment_${shipmentId}`;
    const token = getToken();
    
    if (!token) {
      console.error('No authentication token available for getTasksByShipment');
      throw new Error('Authentication required');
    }
    
    return this.getCacheFirstData(
      cacheKey,
      () => indexedDBService.getTasksByShipment(shipmentId),
      () => {
        console.log('Fetching tasks by shipment from server with token:', token ? 'present' : 'missing');
        return originalTasks.getTasksByShipment(shipmentId);
      }
    );
  }

  async getTasksByUser(userId: string): Promise<Task[] | PaginatedResponse<Task>> {
    const cacheKey = `tasks_user_${userId}`;
    const token = getToken();
    
    if (!token) {
      console.error('No authentication token available for getTasksByUser');
      throw new Error('Authentication required');
    }
    
    return this.getCacheFirstData(
      cacheKey,
      () => indexedDBService.getTasksAssignedToWorker(userId),
      () => {
        console.log('Fetching tasks by user from server with token:', token ? 'present' : 'missing');
        return originalTasks.getTasksByUser(userId);
      }
    );
  }

  // Task mutations (write operations)
  async createTask(taskData: any): Promise<any> {
    try {
      // First, attempt the server operation
      const result = await originalTasks.createTask(taskData);
      
      // If successful, update local cache
      if (result && result.task) {
        await indexedDBService.putTask(result.task);
      }
      
      // Invalidate related caches
      await cacheManager.invalidateCache(['tasks', 'dashboard']);
      
      return result;
    } catch (error) {
      // If offline, queue the operation
      if (!navigator.onLine) {
        await backgroundSyncService.queueOperation('create', 'tasks', taskData);
        throw new Error('Operation queued for when online');
      }
      throw error;
    }
  }

  async updateTask(request: any): Promise<any> {
    try {
      const result = await originalTasks.updateTask(request);
      
      // Update local cache
      if (result && result.task) {
        await indexedDBService.putTask(result.task);
      }
      
      // Invalidate related caches
      await cacheManager.invalidateCache(['tasks', 'dashboard']);
      
      return result;
    } catch (error) {
      if (!navigator.onLine) {
        await backgroundSyncService.queueOperation('update', 'tasks', request);
        throw new Error('Operation queued for when online');
      }
      throw error;
    }
  }

  async deleteTask(taskId: string): Promise<any> {
    try {
      const result = await originalTasks.deleteTask(taskId);
      
      // Remove from local cache
      await indexedDBService.delete('tasks', taskId);
      
      // Invalidate related caches
      await cacheManager.invalidateCache(['tasks', 'dashboard']);
      
      return result;
    } catch (error) {
      if (!navigator.onLine) {
        await backgroundSyncService.queueOperation('delete', 'tasks', { taskId });
        throw new Error('Operation queued for when online');
      }
      throw error;
    }
  }

  async completeTask(request: any): Promise<any> {
    try {
      const result = await originalTasks.completeTask(request);
      
      // Update local cache
      if (result && result.task) {
        await indexedDBService.putTask(result.task);
      }
      
      // Invalidate related caches
      await cacheManager.invalidateCache(['tasks', 'dashboard']);
      
      return result;
    } catch (error) {
      if (!navigator.onLine) {
        await backgroundSyncService.queueOperation('update', 'tasks', {
          ...request,
          operation: 'complete'
        });
        throw new Error('Operation queued for when online');
      }
      throw error;
    }
  }

  // Shipment operations with cache-first strategy
  async getShippings(token?: string, filter?: string): Promise<Shipping[]> {
    const cacheKey = `shipments_${filter || 'all'}`;
    const authToken = token || getToken();
    
    if (!authToken) {
      console.error('No authentication token available for getShippings');
      throw new Error('Authentication required');
    }
    
    return this.getCacheFirstData(
      cacheKey,
      () => indexedDBService.getShipments(),
      async () => {
        console.log('Fetching shipments from server with token:', authToken ? 'present' : 'missing');
        
        // Use user-shippings endpoint which now handles all roles properly
        const user = await getCurrentUser();
        console.log('getShippings: User role detected:', user?.role, 'isPremium:', user?.isPremiumWorker);
        console.log('getShippings: Using user-shippings endpoint for all roles');
        
        return originalApi.getUserShippings(authToken, filter);
      },
      10 * 60 * 1000, // 10 minutes max age
      5 * 60 * 1000   // 5 minutes refresh threshold
    );
  }

  async getUserShippings(token?: string, filter?: string): Promise<Shipping[]> {
    try {
      const user = await getCurrentUser();
      const cacheKey = `shipments_user_${user?._id.$oid}`;
      if (!user) return [];
      return this.getCacheFirstData(
        cacheKey,
        () => indexedDBService.getShipmentsByClient(user.uname || ''),
        () => originalApi.getUserShippings(token || getToken()!, filter),
        10 * 60 * 1000, // 10 minutes max age
        5 * 60 * 1000   // 5 minutes refresh threshold
      );
    } catch (error) {
      console.warn('Failed to get user shipments from cache, falling back to API:', error);
      return originalApi.getUserShippings(token || getToken()!, filter);
    }
  }

  async getShipmentsByClientEmail(clientEmail: string, token?: string): Promise<Shipping[]> {
    try {
      const result = await cacheManager.getCachedShipmentsByClient(clientEmail);
      return result.data;
    } catch (error) {
      console.warn('Cache failed, falling back to direct API:', error);
      return originalApi.getShipmentsByClientEmail(clientEmail, token || getToken()!);
    }
  }

  // Shipment mutations
  async createShipping(shipping: Partial<Shipping>, token?: string): Promise<any> {
    try {
      const result = await originalApi.createShipping(shipping, token || getToken()!);
      
      // Update local cache
      if (result && result.shipping) {
        await indexedDBService.putShipment(result.shipping);
      }
      
      // Invalidate related caches
      await cacheManager.invalidateCache(['shipments', 'dashboard']);
      
      return result;
    } catch (error) {
      if (!navigator.onLine) {
        await backgroundSyncService.queueOperation('create', 'shipments', shipping);
        throw new Error('Operation queued for when online');
      }
      throw error;
    }
  }

  // Dashboard data with smart caching
  async getDashboardData(): Promise<any> {
    try {
      const result = await cacheManager.getCachedDashboardData();
      return result.data;
    } catch (error) {
      console.warn('Dashboard cache failed, building from components:', error);
      
      // Fallback: build dashboard from cached components
      const tasksResult = await this.getTasks();
      const shipments = await this.getShippings();
      
      // Handle both Task[] and PaginatedResponse<Task>
      const tasksArray = Array.isArray(tasksResult) ? tasksResult : tasksResult.tasks;
      
      return {
        tasks: tasksArray.slice(0, 10),
        shipments: shipments.slice(0, 10),
        stats: await indexedDBService.getTaskStats(),
        lastUpdated: Date.now()
      };
    }
  }

  // Quote operations
  async getQuotes(): Promise<any[]> {
    const token = getToken();
    
    if (!token) {
      console.error('No authentication token available for getQuotes');
      throw new Error('Authentication required');
    }
    
    try {
      const result = await cacheManager.getCachedQuotes();
      return result.data;
    } catch (error) {
      console.warn('Quote cache failed:', error);
      try {
        // Fallback to direct API call
        console.log('Fetching quotes from server with token:', token ? 'present' : 'missing');
        const response = await fetch('/.netlify/functions/quotes?includeContacts=true', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Successfully fetched quotes:', Array.isArray(data) ? `${data.length} items` : 'object');
          return data;
        } else {
          throw new Error(`Failed to fetch quotes: ${response.status}`);
        }
      } catch (apiError) {
        console.warn('Direct API call also failed:', apiError);
        throw apiError;
      }
    }
  }

  async getFinalQuotes(): Promise<any[]> {
    const token = getToken();
    
    if (!token) {
      console.error('No authentication token available for getFinalQuotes');
      throw new Error('Authentication required');
    }
    
    try {
      const result = await cacheManager.getCachedFinalQuotes();
      return result.data;
    } catch (error) {
      console.warn('Final quote cache failed:', error);
      try {
        // Fallback to direct API call
        console.log('Fetching final quotes from server with token:', token ? 'present' : 'missing');
        const response = await fetch('/.netlify/functions/quotes?stage=final&includeContacts=true', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Successfully fetched final quotes:', Array.isArray(data) ? `${data.length} items` : 'object');
          return data;
        } else {
          throw new Error(`Failed to fetch final quotes: ${response.status}`);
        }
      } catch (apiError) {
        console.warn('Direct API call also failed:', apiError);
        throw apiError;
      }
    }
  }

  // Contact operations
  async getContacts(): Promise<any[]> {
    const token = getToken();
    
    if (!token) {
      console.error('No authentication token available for getContacts');
      throw new Error('Authentication required');
    }

    try {
      console.log('Fetching contacts from server with token:', token ? 'present' : 'missing');
      const response = await fetch('/.netlify/functions/quotes?operation=contacts', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Successfully fetched contacts:', Array.isArray(data) ? `${data.length} items` : 'object');
        return data;
      } else {
        throw new Error(`Failed to fetch contacts: ${response.status}`);
      }
    } catch (error) {
      console.warn('Contacts fetch failed:', error);
      throw error;
    }
  }

  // Offline-aware operations
  async performOfflineAwareOperation<T>(
    operation: () => Promise<T>,
    fallbackValue?: T,
    queueData?: { type: 'create' | 'update' | 'delete'; table: string; data: any }
  ): Promise<T> {
    try {
      if (navigator.onLine) {
        return await operation();
      } else {
        if (queueData) {
          await backgroundSyncService.queueOperation(
            queueData.type,
            queueData.table,
            queueData.data
          );
        }
        
        if (fallbackValue !== undefined) {
          return fallbackValue;
        }
        
        throw new Error('Operation not available offline');
      }
    } catch (error) {
      if (!navigator.onLine && fallbackValue !== undefined) {
        return fallbackValue;
      }
      throw error;
    }
  }

  // Worker-specific filtering
  async getWorkerTasks(workerId?: string, forceRefresh = false): Promise<Task[]> {
    const user = await getCurrentUser(forceRefresh);
    const targetWorkerId = workerId || user?._id;
    
    if (!targetWorkerId) {
      return [];
    }

    try {
      // The server decides which tasks this person may see, the same way it decides
      // which shipments they may see.
      const result = await cacheManager.getCachedTasks();
      return result.data;
    } catch (error) {
      console.warn('Worker task cache failed:', error);
      return [];
    }
  }

  async getWorkerShipments(workerId?: string, forceRefresh = false): Promise<Shipping[]> {
    const user = await getCurrentUser(forceRefresh);
    console.log('getWorkerShipments called for user:', user?.uname, 'isPremium:', user?.isPremiumWorker);
    
    try {
      // Use cache manager which now handles both premium and standard workers
      const result = await cacheManager.getCachedShipments();
      console.log('getWorkerShipments: Got result from cache manager:', result);
      
      // Extract shipments from cache result
      const shipments = result.data || [];
      console.log('getWorkerShipments: Extracted shipments:', shipments.length);
      return shipments;
    } catch (error) {
      console.warn('Worker shipment cache failed:', error);
      
      // Fallback: manual logic for when cache fails
      if (user?.isPremiumWorker) {
        console.log('Fallback: Premium worker - getting all shipments');
        try {
          const allShipments = await this.getShippings();
          const filteredShipments = allShipments.filter((shipment: any) => 
            !shipment.hiddenFromPremiumWorkers
          );
          console.log('Fallback shipments for premium worker:', filteredShipments.length);
          return filteredShipments;
        } catch (fallbackError) {
          console.error('Fallback failed for premium worker:', fallbackError);
          return [];
        }
      } else {
        // The server already returns exactly the shipments this person may see, so the
        // fallback asks it rather than narrowing again by tasks.
        try {
          return await this.getShippings();
        } catch (error) {
          console.warn('Staff shipment fallback failed:', error);
          return [];
        }
      }
    }
  }

  private refreshIntervals = new Map<string, number>();
  private lastRefreshTimes = new Map<string, number>();
  
  // Smart refresh operations with throttling
  async refreshTasks(background: boolean = false): Promise<void> {
    const key = 'tasks_refresh';
    const minInterval = 30000; // 30 seconds minimum between refreshes
    
    if (background) {
      const lastRefresh = this.lastRefreshTimes.get(key) || 0;
      const now = Date.now();
      
      if (now - lastRefresh < minInterval) {
        console.debug('Tasks refresh throttled, too soon since last refresh');
        return;
      }
      
      this.lastRefreshTimes.set(key, now);
      
      // Use requestIdleCallback for better performance
      const scheduleRefresh = (callback: () => void) => {
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(callback, { timeout: 10000 });
        } else {
          setTimeout(callback, 2000);
        }
      };
      
      scheduleRefresh(async () => {
        if (!navigator.onLine || document.hidden) return;
        
        try {
          await backgroundSyncService.syncTasks();
        } catch (error) {
          console.warn('Background task refresh failed:', error);
        }
      });
    } else {
      this.lastRefreshTimes.set(key, Date.now());
      await backgroundSyncService.syncTasks();
    }
  }

  async refreshShipments(background: boolean = false): Promise<void> {
    const key = 'shipments_refresh';
    const minInterval = 60000; // 1 minute minimum between refreshes
    
    if (background) {
      const lastRefresh = this.lastRefreshTimes.get(key) || 0;
      const now = Date.now();
      
      if (now - lastRefresh < minInterval) {
        console.debug('Shipments refresh throttled, too soon since last refresh');
        return;
      }
      
      this.lastRefreshTimes.set(key, now);
      
      const scheduleRefresh = (callback: () => void) => {
        if ('requestIdleCallback' in window) {
          (window as any).requestIdleCallback(callback, { timeout: 15000 });
        } else {
          setTimeout(callback, 3000);
        }
      };
      
      scheduleRefresh(async () => {
        if (!navigator.onLine || document.hidden) return;
        
        try {
          await backgroundSyncService.syncShipments();
        } catch (error) {
          console.warn('Background shipment refresh failed:', error);
        }
      });
    } else {
      this.lastRefreshTimes.set(key, Date.now());
      await backgroundSyncService.syncShipments();
    }
  }

  // Memory cleanup method
  async cleanupMemory(): Promise<void> {
    try {
      // Clear old refresh callbacks
      this.refreshCallbacks.clear();
      
      // Clear old refresh tracking
      const now = Date.now();
      const maxAge = 10 * 60 * 1000; // 10 minutes
      
      for (const [key, timestamp] of this.lastRefreshTimes.entries()) {
        if (now - timestamp > maxAge) {
          this.lastRefreshTimes.delete(key);
        }
      }
      
      // Clear background refresh queue if it gets too large
      if (this.backgroundRefreshQueue.size > 10) {
        this.backgroundRefreshQueue.clear();
      }
      
      // Trigger garbage collection if available
      if ('gc' in window) {
        (window as any).gc();
      }
      
      console.debug('Memory cleanup completed');
    } catch (error) {
      console.warn('Memory cleanup failed:', error);
    }
  }

  // Force refresh for when user explicitly wants fresh data
  async forceRefresh(dataTypes?: string[]): Promise<void> {
    const types = dataTypes || ['tasks', 'shipments'];
    
    for (const type of types) {
      await cacheManager.invalidateCache(type);
    }
    
    await backgroundSyncService.syncData({ force: true });
  }

  // Clear data functionality with selective clearing
  async clearAllData(): Promise<void> {
    await this.cleanup(); // Clean up service state first
    await indexedDBService.clearAllData();
    await cacheManager.clearAllCache();
  }

  // Selective cache clearing to manage memory
  async clearExpiredCache(): Promise<void> {
    try {
      const now = Date.now();
      const maxAge = 30 * 60 * 1000; // 30 minutes
      
      // Clear old cached data based on metadata
      const cacheKeys = ['tasks', 'shipments', 'quotes', 'contacts'];
      
      for (const key of cacheKeys) {
        try {
          const metadata = await indexedDBService.getMetadata(key);
          if (metadata && (now - metadata.lastUpdated) > maxAge) {
            await cacheManager.invalidateCache(key);
            console.debug(`Cleared expired cache for ${key}`);
          }
        } catch (error) {
          console.debug(`Could not check cache age for ${key}:`, error);
        }
      }
    } catch (error) {
      console.warn('Cache expiry cleanup failed:', error);
    }
  }

  // Get cache size for monitoring
  async getCacheSize(): Promise<{
    indexedDB: number;
    memory: number;
    items: { [key: string]: number };
  }> {
    try {
      const cacheStats = await cacheManager.getCacheStats();
      
      return {
        indexedDB: cacheStats.totalSize || 0,
        memory: this.refreshCallbacks.size + this.backgroundRefreshQueue.size,
        items: cacheStats.storage || {}
      };
    } catch (error) {
      console.warn('Failed to get cache size:', error);
      return {
        indexedDB: 0,
        memory: 0,
        items: {}
      };
    }
  }

  // Health check and status
  async getStatus(): Promise<{
    online: boolean;
    cacheHealth: any;
    syncStatus: any;
    lastSync: number;
  }> {
    const cacheStats = await cacheManager.getCacheStats();
    const syncStatus = await backgroundSyncService.getStatus();
    
    return {
      online: navigator.onLine,
      cacheHealth: cacheStats,
      syncStatus,
      lastSync: syncStatus.lastSync
    };
  }

  // Batch operations for better performance
  async batchUpdateTasks(updates: Array<{ taskId: string; updates: any }>): Promise<any[]> {
    const results = [];
    
    for (const update of updates) {
      try {
        const result = await this.updateTask(update);
        results.push({ success: true, taskId: update.taskId, result });
      } catch (error) {
        results.push({ 
          success: false, 
          taskId: update.taskId, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
      }
    }
    
    return results;
  }

  // Initialize the service
  async initialize(): Promise<void> {
    try {
      // Initialize IndexedDB
      await indexedDBService.init();
      
      // Warming the cache and the full sync download every record, so they run in
      // the background. Waiting on them held the whole site on its loading screen.
      cacheManager.warmCache()
        .then(() => backgroundSyncService.syncData({ background: true }))
        .catch((error) => console.warn('Background cache sync failed:', error));
      
      // Set up periodic memory cleanup
      setInterval(() => {
        this.cleanupMemory();
      }, 5 * 60 * 1000); // Every 5 minutes
      
      // Listen for page visibility changes to pause background operations
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          console.debug('Page hidden, pausing background operations');
        } else {
          console.debug('Page visible, resuming background operations');
        }
      });
      
    } catch (error) {
      console.warn('API service initialization failed:', error);
    }
  }

  // Cleanup method for component unmounting
  async cleanup(): Promise<void> {
    try {
      // Clear all intervals
      for (const intervalId of this.refreshIntervals.values()) {
        clearInterval(intervalId);
      }
      this.refreshIntervals.clear();
      
      // Clear all callbacks
      this.refreshCallbacks.clear();
      
      // Clear background refresh queue
      this.backgroundRefreshQueue.clear();
      
      // Clear tracking maps
      this.lastRefreshTimes.clear();
      
      console.debug('API service cleanup completed');
    } catch (error) {
      console.warn('API service cleanup failed:', error);
    }
  }

  // User data methods
  async getUserUpdates(token?: string): Promise<any[]> {
    try {
      return originalApi.getUserUpdates(token || getToken()!);
    } catch (error) {
      console.warn('Get user updates failed:', error);
      return [];
    }
  }

  async getUserFiles(token?: string): Promise<any[]> {
    try {
      return originalApi.getUserFiles(token || getToken()!);
    } catch (error) {
      console.warn('Get user files failed:', error);
      return [];
    }
  }

  // Pass-through methods for operations that don't need caching
  async login(email: string, password: string) {
    return originalApi.login(email, password);
  }

  async logout() {
    // Clear cache on logout
    await this.clearAllData();
    return originalApi.logout();
  }

  async sendEmail(emailData: any) {
    return originalApi.sendEmail(emailData);
  }

  async calculateShippingCost(request: any) {
    return originalApi.calculateShippingCost(request);
  }

  // Debug method to test authentication
  // Uses quotes endpoint with limit=1 as a lightweight auth check
  async testAuthentication(): Promise<{ authenticated: boolean; user?: any; error?: string }> {
    try {
      const token = getToken();
      console.log('Testing authentication with token:', { hasToken: !!token, tokenLength: token?.length });
      
      if (!token) {
        return { authenticated: false, error: 'No token found' };
      }
      
      const response = await fetch('/.netlify/functions/quotes?limit=1', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        return { authenticated: false, error: 'Token invalid or expired' };
      }
      
      if (!response.ok) {
        return { authenticated: false, error: `Server error: ${response.status}` };
      }

      const user = await getCurrentUser();
      console.log('Authentication test result: authenticated', { user: user?.uname });
      return { authenticated: true, user };
    } catch (error) {
      console.error('Authentication test failed:', error);
      return { authenticated: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }
}

// Create singleton instance
const cachedApiService = new CachedApiService();

// Export both the service and original APIs for backward compatibility
export default cachedApiService;
export { CachedApiService };

// Re-export original API methods that don't need caching
export {
  login,
  logout,
  clearBrowserData,
  getUser,
  getUsers,
  createUser,
  updateUser,
  createShippingUpdate,
  updateShippingUpdate,
  uploadShipmentFiles,
  getFilesByFolder,
  sendNotification,
  getDocuments,
  calculateShippingCost,
  sendEmail,
  getEmailTemplates,
  getAvailableRecipients,
  getEmails,
  getRecentEmails,
  getEmailById,
  saveEmail,
  updateEmailStatus,
  deleteEmail,
  getEmailStats,
  getShipmentPreview,
  getUserUpdates,
  getUserFiles,
  getShippingUpdates
} from './api';

// Re-export task methods that don't need special caching
export {
  getTaskDashboard,
  getTaskTemplates,
  updateTaskTemplates,
  generateTasks,
  assignTask,
  completeBatchTasks,
  incompleteBatchTasks,
  getNotifications,
  getNotificationDashboard,
  getShipmentUpdates,
  createNotification,
  markNotificationsAsRead,
  updateNotification,
  deleteNotification
} from './tasks';

